import React from 'react';
import { HiOutlineBanknotes, HiOutlineCalendarDays, HiOutlineCheckBadge } from 'react-icons/hi2';

import { Modal } from 'Core/components/Layout';
import { OutlineButton } from 'Core/components/Button';
import { formatCurrency } from 'Core/utils/numberFormat';
import { formatEpochDateTime, getApplicationRef } from 'Modules/financing/utils/exchangeStatus';

const humanizeStatus = (value) =>
  String(value)
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const asCurrency = (value) => formatCurrency(Number(value) || 0, { withDecimals: true });

const isPresent = (value) => value !== null && typeof value !== 'undefined' && value !== '';

// The two figures that carry the story — surfaced as tiles above the detail list.
const HIGHLIGHTS = [
  { key: 'financed', label: 'Financed', Icon: HiOutlineBanknotes, format: asCurrency },
  {
    key: 'monthly_payment',
    label: 'Monthly Payment',
    Icon: HiOutlineCalendarDays,
    format: asCurrency,
  },
];

// Everything else the platform sends back per previous application, in display order.
// Fields the response omits are skipped instead of rendering an empty row.
const FIELDS = [
  {
    key: 'application_status',
    label: 'Application Status',
    format: humanizeStatus,
    emphasis: true,
  },
  { key: 'amount_to_pay', label: 'Amount to Pay', format: asCurrency },
  { key: 'already_paid_amount', label: 'Already Paid', format: asCurrency },
  { key: 'financing_down_payment', label: 'Down Payment', format: asCurrency },
  { key: 'down_payment_status', label: 'Down Payment Status', format: humanizeStatus },
  { key: 'created_at', label: 'Created', format: formatEpochDateTime },
];

const cardTitle = (application) => {
  const ref = getApplicationRef(application);
  return isPresent(ref) ? `Request #${ref}` : 'Application';
};

function PreviousApplicationCard({ application, index }) {
  const highlights = HIGHLIGHTS.filter(({ key }) => isPresent(application?.[key]));
  const rows = FIELDS.filter(({ key }) => isPresent(application?.[key]));
  const testId = (key) => `previous-application-${index}-${key}`;

  return (
    <article
      className="rounded-xl border border-solid border-slate-200 bg-white p-3.5"
      data-testid={`previous-application-card-${index}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4
          className="!mb-0 text-sm font-bold text-slate-900"
          data-testid={`previous-application-card-title-${index}`}
        >
          {cardTitle(application)}
        </h4>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-green-600 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-white shadow-sm">
          <span className="h-1.5 w-1.5 rounded-full bg-white" aria-hidden="true" />
          Funded &amp; Active
        </span>
      </div>

      {highlights.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-3">
          {highlights.map(({ key, label, Icon: TileIcon, format }) => (
            <div
              key={key}
              className="flex min-w-0 flex-1 items-center gap-3 rounded-xl border border-solid border-slate-200 bg-gradient-to-b from-slate-50 to-white px-3.5 py-3 [flex-basis:180px]"
            >
              <span
                aria-hidden="true"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600"
              >
                <TileIcon className="h-[18px] w-[18px]" />
              </span>
              <div className="min-w-0">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  {label}
                </div>
                <div
                  className="text-lg font-extrabold tabular-nums leading-tight text-slate-900"
                  data-testid={testId(key)}
                >
                  {format(application[key])}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <dl className="!mb-0 mt-3">
        {rows.map(({ key, label, format, emphasis }, rowIndex) => (
          <div
            key={key}
            className={`flex items-center justify-between gap-4 py-2.5 ${
              rowIndex === 0 ? '' : 'border-0 border-t border-solid border-slate-100'
            }`}
          >
            <dt className="text-sm text-slate-500">{label}</dt>
            <dd
              className={`!mb-0 text-right text-sm font-semibold tabular-nums ${
                emphasis ? 'text-green-700' : 'text-slate-900'
              }`}
              data-testid={testId(key)}
            >
              {format(application[key])}
            </dd>
          </div>
        ))}
      </dl>
    </article>
  );
}

export default function PreviousApplicationModal({
  isOpen,
  onClose,
  previousApplications = [],
  requestedAt,
}) {
  const applications = previousApplications.filter(Boolean);
  const isPlural = applications.length > 1;

  return (
    <Modal isOpen={isOpen} className="w-full max-w-lg">
      <div className="rounded-xl border border-solid border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-4">
        <div className="flex items-start gap-3.5">
          <span
            aria-hidden="true"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600 ring-4 ring-white/70"
          >
            <HiOutlineCheckBadge className="h-6 w-6" />
          </span>
          <div className="min-w-0 flex-1">
            <h3
              className="!mb-0 font-bold text-slate-900"
              data-testid="previous-application-modal-title"
            >
              {isPlural ? 'Previous Applications' : 'Previous Application'}
            </h3>
          </div>
        </div>

        <p className="!mb-0 mt-3.5 text-sm leading-relaxed text-slate-600">
          {isPlural
            ? 'These applications are funded and remain in effect. Nothing has been refunded or cancelled, and they stay active until the exchange is funded.'
            : 'This application is funded and remains in effect. Nothing has been refunded or cancelled, and it stays active until the exchange is funded.'}
        </p>
      </div>

      <div className="mt-4 flex flex-col gap-3" data-testid="previous-application-details">
        {applications.map((application, index) => (
          <PreviousApplicationCard
            key={getApplicationRef(application) ?? index}
            application={application}
            index={index}
          />
        ))}
      </div>

      {requestedAt && (
        <p
          className="mt-3 !mb-0 text-xs text-slate-400"
          data-testid="previous-application-requested"
        >
          Exchange last requested on {requestedAt}.
        </p>
      )}

      <hr className="my-4 border-0 border-t border-solid border-slate-200" />

      <div className="flex justify-end">
        <OutlineButton type="button" onClick={onClose} data-testid="btn-close-previous-application">
          Close
        </OutlineButton>
      </div>
    </Modal>
  );
}
